<template>
  <div class="game-activity-container">
    <!-- 页面标题 -->
    <div class="header-content" style="align-items: center;">
      <div style="font-size: 24px; color: white;">玩法活动数据</div>
      <div style="font-size: 20px; font-weight: bold; margin-top: 1%;">{{ gameName }}</div>
    </div>

    <!-- 折线图：畅销榜排名 -->
    <div style="text-align: center;">
      <h2>畅销榜排名</h2>
      <!-- 判断 bestsellerData 是否为空 -->
      <div v-if="!bestsellerData || Object.keys(bestsellerData).length === 0" style="color: red; font-weight: bold;">
        暂无上榜数据
      </div>
      <div v-else style="display: inline-block;">
        <v-chart v-if="bestsellerChartOptions && Object.keys(bestsellerChartOptions).length" :option="bestsellerChartOptions" style="width: 600px; height: 500px; margin-bottom: 20px; background-color: rgba(255, 255, 255, 0.95);" />
      </div>
    </div>

    <!-- 折线图：玩法榜排名 -->
    <div style="text-align: center;">
      <h2>玩法榜排名</h2>
      <!-- 判断 rankingData 是否为空 -->
      <div v-if="!rankingData || Object.keys(rankingData).length === 0" style="color: red; font-weight: bold;">
        暂无上榜数据
      </div>
      <div v-else style="display: inline-block;">
        <v-chart v-if="rankingChartOptions && Object.keys(rankingChartOptions).length" :option="rankingChartOptions" style="width: 600px; height: 500px; margin-bottom: 20px; background-color: rgba(255, 255, 255, 0.95);" />
      </div>
    </div>

    <!-- 活动详情 -->
    <div style="text-align: center;">
      <h2>活动详情</h2>

      <!-- Check if there are no tasks -->
      <div v-if="!taskData.length" style="color: red; font-weight: bold;">
        暂无该游戏活动数据
      </div>

      <div v-else style="display: inline-block;">
        <label>选择日期:</label>
        <el-button type="primary" @click="openDateSelectionDialog" style="width: 100%;">
          {{ selectedDate }}
        </el-button>
      </div>

      <!-- 活动表格 -->
      <div class="table-container">
        <el-table
            v-if="selectedTask && selectedTask.rank_list_task_stage && selectedTask.rank_list_task_stage.length > 0"
            :data="selectedTask.rank_list_task_stage"
            style="width: 80%"
            :border="true"
            :header-row-style="headerRowStyle"
            :header-cell-style="headerCellStyle"
        >
          <el-table-column label="排名" align="center" min-width="150">
            <template v-slot="scope">
              <span>{{ formatRank(scope.row.rank_from, scope.row.rank_to) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="任务值" align="center" min-width="150">
            <template v-slot="scope">
              <span>{{ formatScore(scope.row.total_score_amount) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="现金奖励" align="center" min-width="150">
            <template v-slot="scope">
              <span>{{ formatReward(scope.row.reward_amount) }}</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 活动表格：新逻辑 -->
        <el-table
            v-else-if="selectedTask && selectedTask.stage_list && selectedTask.stage_list.length > 0"
            :data="selectedTask.stage_list"
            style="width: 80%"
            :border="true"
            :header-row-style="headerRowStyle"
            :header-cell-style="headerCellStyle"
        >
          <el-table-column label="有效开播" align="center" min-width="150">
            <template v-slot="scope">
              <span>{{ getTargetValue(scope.row.condition_list, 1) }}天</span>
            </template>
          </el-table-column>
          <el-table-column label="累计音浪" align="center" min-width="150">
            <template v-slot="scope">
              <span>{{ getTargetValue(scope.row.condition_list, 2) }}音浪</span>
            </template>
          </el-table-column>
          <el-table-column label="现金奖励" align="center" min-width="150">
            <template v-slot="scope">
              <span>{{ getRewardAmount(scope.row.reward_list) }}元</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 日期选择对话框 -->
    <el-dialog
        v-model="dateSelectionDialogVisible"
        title="选择日期"
        width="400px"
        :style="{ top: dialogTop + 'px' }"
    >
      <el-radio-group v-model="selectedDate" style="width: 100%">
        <el-radio-button v-for="(date, index) in taskDates" :key="index" :label="date">
          {{ date }}
        </el-radio-button>
      </el-radio-group>
      <template v-slot:footer>
        <el-button @click="dateSelectionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="onDateSelected">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { ElTable, ElTableColumn, ElButton, ElDialog, ElRadioGroup, ElRadioButton } from 'element-plus'
import VChart from 'vue-echarts'

const gameName = ref('')
const taskData = ref([])  // 存储任务数据
const bestsellerChartOptions = ref({})
const rankingChartOptions = ref({})
const taskDates = ref([])  // 存储任务日期
const selectedDate = ref('')
const dateSelectionDialogVisible = ref(false)
const selectedTask = ref(null) // 存储当前选择的任务

// 响应式的 bestsellerData 和 rankingData
const bestsellerData = ref(null)
const rankingData = ref(null)

// 获取URL中的game_name参数
const urlParams = new URLSearchParams(window.location.search)
const gameNameFromUrl = urlParams.get('game_name')

// 获取玩法活动数据
const fetchGameActivityData = async (gameName) => {
  try {
    const response = await axios.get(`https://www.hudongwanfa.cn/api/game_activity_rank?game_name=${gameName}`)
    const data = response.data.data
    processGameData(data)
  } catch (error) {
    console.error(error)
  }
}

// 处理返回的数据
const processGameData = (data) => {
  const fetchedBestsellerData = data.bestseller
  const fetchedRankingData = data.ranking
  const tasks = data.tasks

  bestsellerData.value = fetchedBestsellerData
  rankingData.value = fetchedRankingData

  if (fetchedBestsellerData) {
    bestsellerChartOptions.value = generateLineChartOptions(fetchedBestsellerData, '畅销榜排名')
  }
  if (fetchedRankingData) {
    rankingChartOptions.value = generateLineChartOptions(fetchedRankingData, '玩法榜排名')
  }

  // 去重并解析任务数据
  const uniqueTasks = []
  const taskMap = new Map()
  tasks.forEach(task => {
    const taskTime = `${task.start_time} - ${task.end_time}`
    if (!taskMap.has(taskTime)) {
      taskMap.set(taskTime, task)
      uniqueTasks.push(task)
    }
  })

  taskData.value = uniqueTasks.map(task => ({
    task_name: task.task_name,
    start_time: task.start_time,
    end_time: task.end_time,
    rank_list_task_stage: JSON.parse(task.rank_list_task_stage),
    stage_list: JSON.parse(task.stage_list)
  }))

  taskDates.value = uniqueTasks.map(task => formatDate(new Date(task.start_time)) + ' - ' + formatDate(new Date(task.end_time)))

  selectedDate.value = taskDates.value[0]
  onDateSelected()
}

// 格式化日期为 'yyyy-MM-dd' 格式
const formatDate = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 获取condition_list或reward_list中的值
const getTargetValue = (conditionList, type) => {
  const item = conditionList.find(c => c.type === type)
  return item ? item.target_value : ''
}

const getRewardAmount = (rewardList) => {
  const item = rewardList.find(r => r.type === 1)
  return item ? item.Amount : ''
}

// 使用 ref 来存储 startDate 和 endDate
const startDate = ref(null);
const endDate = ref(null);
const highlightIndexes = ref([]);
const visualMap = ref({});

// 格式化折线图数据
const generateLineChartOptions = (data, title) => {
  const dates = Object.keys(data); // 获取所有日期
  const rankings = Object.values(data); // 获取所有排名

  // 获取选中的日期范围

  // 计算 startDate 和 endDate
  const [start, end] = selectedDate.value.split(' - ').map(date => new Date(date)); // 获取选择的日期范围
  startDate.value = start;
  endDate.value = end;

  // 获取日期范围内的所有日期的索引
  highlightIndexes.value = dates.filter(date => {
    const currentDate = new Date(date);
    return currentDate >= startDate.value && currentDate <= endDate.value;
  }).map(date => dates.indexOf(date)); // 获取范围内日期的索引

  // 获取排名中的最大值，用于设置 Y 轴的最大值
  const maxRank = Math.max(...rankings);

  // 设置 Y 轴最大值为最大排名的基础上加 2（即设置为 0 到 maxRank + 2）
  const maxYAxisValue = Math.max(maxRank + 2, 10); // 确保 Y 轴至少是 0 到 13

  // 计算 Y 轴的间隔
  const interval = Math.floor(maxYAxisValue / 5); // 根据最大值设置每个刻度的间隔，使刻度不超过 5 个

  // 构建折线图的数据
  const seriesData = [{
    name: title,
    type: 'line',
    data: rankings, // 使用排名数组作为 y 轴数据
    itemStyle: {
      color: (params) => {
        // 使用 visualMap 来改变颜色
        return highlightIndexes.value.includes(params.dataIndex) ? 'red' : '#3398DB';
      },
    },
  }];

  // 构建 visualMap 组件的配置
  visualMap.value = {
    show: false,
    dimension: 0,
    pieces: []
  };

  // 根据 highlightIndexes 来动态构建 pieces
  if (highlightIndexes.value.length > 0) {
    const firstIndex = highlightIndexes.value[0];
    const lastIndex = highlightIndexes.value[highlightIndexes.value.length - 1];

    visualMap.value.pieces.push({
      lte: firstIndex,
      color: '#3398DB',  // 默认颜色
    });

    visualMap.value.pieces.push({
      gt: firstIndex,
      lte: lastIndex,
      color: 'red', // 高亮的颜色
    });

    visualMap.value.pieces.push({
      gt: lastIndex,
      color: '#3398DB',  // 默认颜色
    });
  } else {
    const lengthTotal = dates.length - 1;
    visualMap.value.pieces.push({
      gt: 0,
      lte: lengthTotal,
      color: '#3398DB',  // 默认颜色
    });
  }

  return {
    title: {
      text: title,
      left: 'center'
    },
    xAxis: {
      type: 'category',
      data: dates, // 使用日期作为 x 轴数据
      boundaryGap: false,
      name: '日期',
      nameLocation: 'middle',
      nameGap: 40,
      axisLabel: {
        rotate: 45,  // 旋转x轴标签，防止重叠
        interval: 5  // 设置显示的刻度间隔
      }
    },
    yAxis: {
      type: 'value',
      inverse: true,
      name: '排名',
      nameLocation: 'middle',
      nameRotate: 90,
      nameGap: 40,
      min: 0,  // 设置 y 轴的最小值为 0
      max: maxYAxisValue,  // 设置 y 轴的最大值
      interval: interval,  // 设置 y 轴刻度间隔
    },
    series: seriesData,
    visualMap: visualMap,
  };
};

// 格式化排名显示
const formatRank = (rankFrom, rankTo) => {
  return rankFrom === rankTo ? `${rankFrom}` : `${rankFrom}-${rankTo}`
}

// 格式化任务值显示为"XX万"
const formatScore = (score) => {
  return (score / 10000) + '万'
}

// 格式化现金奖励显示
const formatReward = (rewardAmount) => {
  return rewardAmount ? rewardAmount.toLocaleString() + '元' : '暂无奖励';
}

// 选择日期后更新内容
const dialogTop = ref(100)  // 默认给个 100p
function openDateSelectionDialog() {
  // 按照你的公式：768 / window.innerWidth * window.scrollY + 100
  dialogTop.value = 768 / window.innerWidth * window.scrollY + 100
  dateSelectionDialogVisible.value = true
}

// 确定日期后更新显示内容
const onDateSelected = () => {
  dateSelectionDialogVisible.value = false;

  // 确保选中的日期范围正确
  console.log('Selected date:', selectedDate.value);  // 检查选择的日期范围

  // 根据所选日期更新表格内容
  const selectedTaskData = taskData.value.find(task => {
    const taskStart = formatDate(new Date(task.start_time));
    const taskEnd = formatDate(new Date(task.end_time));
    return `${taskStart} - ${taskEnd}` === selectedDate.value;
  });

  if (selectedTaskData) {
    // 保留原始 rank_list_task_stage
    selectedTask.value = {
      ...selectedTaskData,
      // 不修改 rank_list_task_stage
    }
  }

  // 重算日期范围并更新图表
  generateLineChartOptions(bestsellerData.value, '畅销榜排名');
  generateLineChartOptions(rankingData.value, '玩法榜排名');

  console.log('Selected task:', selectedTask.value);
}

// 12. 表头单元格样式
function headerCellStyle() {
  return {
    backgroundColor: '#4a649e',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    fontSize: '16px'
  }
}

// 13. 表头行样式
function headerRowStyle() {
  return { backgroundColor: '#f2f2f2', fontWeight: 'bold' }
}

// 页面加载时自动请求数据
onMounted(() => {
  gameName.value = gameNameFromUrl
  fetchGameActivityData(gameNameFromUrl)
})
</script>

<style scoped>
/* 自定义样式 */
.table-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.el-table {
  margin: 0 auto;
  width: 80%; /* Adjust the width of the table as per your requirement */
}

.chart-box {
  margin: 20px 0;
  background-color: #ffe;
}

.el-radio-group {
  display: flex;
  gap: 20px;
}

.el-table .cell {
  text-align: center;
}

.el-button {
  margin: 10px;
}
</style>